// 🔧 vote_audit.js
// ✅ Purpose: Shows who voted for a specific item (admin-only view)
// ✅ Features:
// - Loads voters from /events/{eventId}/votes where cid matches
// - Joins voter names from /users
// - Joins car info from /events/{eventId}/cars
// - Uses `useEventContext` to get eventId and eventslug
// - Shows "Loading..." until all data is ready
// - Fully commented for clarity

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useEventContext } from "../../hooks/use_event_context"; // ✅ use standard context
import {
  getDoc,
  getDocs,
  doc,
  collection,
} from "firebase/firestore";
import { db } from "../../firebase";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from '../../utils/logger';
import {
  getEventVotingType,
  isStandardVotingEvent,
} from "../../utils/event_voting_type_helpers";

const VoteAudit = () => {
  const { cid } = useParams(); // car id
  const {
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });
  const [votes, setVotes] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const votingType = getEventVotingType(eventDoc);
  const isStandardVoting = isStandardVotingEvent(eventDoc);

  useEffect(() => {
    const loadData = async () => {
      logger.log("🚀 Starting data load in VoteAudit", {});
      if (!eventId || !cid) {
        logger.log("⏳ Waiting for eventId or cid", {
          eventId,
          cid,
        });
        return;
      }

      logger.log("✅ Loading votes for car:", {
        value: cid
      });
      const votesSnap = await getDocs(
        collection(db, "events", eventId, "votes")
      );
      // 🗳 STANDARD VOTING ONLY
      // Assumes one vote document represents one vote.
      // Future voting types (ranked choice, points, scorecard)
      // will require different audit logic.
      const allVotes = votesSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const filteredVotes = allVotes.filter(
        (vote) => vote.cid === cid
      );

      setVotes(filteredVotes);

      logger.log("✅ Votes loaded:", {
        value: filteredVotes.length
      });

      const usersSnap = await getDocs(
        collection(db, "users")
      );

      const users = usersSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const usersMap = Object.fromEntries(
        users.map((u) => [u.id, u])
      );

      setUsersMap(usersMap);

      logger.log("✅ Users loaded:", {
        value: users.length
      });

      const carDoc = await getDoc(
        doc(
          db,
          "events",
          eventId,
          "cars",
          cid
        )
      );
      setCar(carDoc.exists() ? { id: carDoc.id, ...carDoc.data() } : null);
      logger.log("✅ Car loaded:", {
        value: carDoc.id
      });

      setLoading(false);
    };

    loadData();
  }, [eventId, cid]);

  if (eventLoading || !ready || loading || !car) return <FullPageSpinner message="Loading Audit..." />
  if (!isStandardVoting) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          Vote audit has not been implemented for
          <strong> {votingType} </strong>
          voting.
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <h2 className="mb-4">🧾 Vote Audit for: {car.description} ({car.regcnt})</h2>
      <ul className="list-group">
        {votes.map(vote => {
          const user = usersMap[vote.uid];
          return (
            <li key={vote.id} className="list-group-item">
              {user ? `${user.firstName} ${user.lastName} (${user.email})` : "❓ Unknown voter"}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default VoteAudit;
